import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";

import { CartItemType } from "../../interfaces/products/cartItem.interface";
import { getCartItem, saveInLocalStorage } from "../../config/localStorage";
import AppContext from "../../config/appContext";
import { getCurrencySymbol } from "../../constants/currency";

interface CartProps {
  businessInfo: Record<string, string | number | boolean | any>;
  setComponentToDisplay: (selection: string) => void;
}

const Cart: React.FC<CartProps> = ({ businessInfo, setComponentToDisplay }) => {
  const { slug, paymentInfo } = businessInfo;
  const theme = useTheme();
  const { cartUpdated, setCartUpdated } = useContext(AppContext);

  const [cartItems, setCartItems] = useState<CartItemType[]>([]);
  const [subTotal, setSubTotal] = useState<number>(0);
  const [selectedReceptionMethod, setSelectedReceptionMethod] =
    useState<string>("");

  const handleIncreaseQty = (index: number) => {
    const newCartItems = [...cartItems];
    newCartItems[index].qty += 1;
    setCartItems(newCartItems);
    updateCart(newCartItems);
  };

  const handleDecreaseQty = (index: number) => {
    const newCartItems = [...cartItems];
    if (newCartItems[index].qty > 1) {
      newCartItems[index].qty -= 1;
      setCartItems(newCartItems);
      updateCart(newCartItems);
    }
  };

  const handleRemoveItem = (index: number) => {
    const newCartItems = cartItems.filter((_, i) => i !== index);
    setCartItems(newCartItems);
    updateCart(newCartItems);
  };

  const updateCart = (updatedItems: CartItemType[]) => {
    let currentCart = getCartItem(`cart-${slug}`);
    currentCart.items = updatedItems;

    saveInLocalStorage(`cart-${slug}`, currentCart);
    setCartUpdated(!cartUpdated);
  };

  const getSubtotal = () => {
    const subtotal = cartItems.reduce((total, item) => {
      return total + item.price * item.qty;
    }, 0);
    setSubTotal(Number(subtotal.toFixed(2)));
  };

  const handleReceptionMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedReceptionMethod(event.target.value);
    const tempCart = getCartItem(`cart-${slug}`);
    tempCart.receptionMethod = event.target.value;
    saveInLocalStorage(`cart-${slug}`, tempCart);
  };

  const onCheckoutPressed = () => {
    setComponentToDisplay("CUSTOMERINFORMATION");
  };

  useEffect(() => {
    const tempCart = getCartItem(`cart-${slug}`);
    if (tempCart) {
      setCartItems(tempCart?.items);
      setSelectedReceptionMethod(tempCart?.receptionMethod);
    }
  }, [cartUpdated, slug]);

  useEffect(() => {
    getSubtotal();
  }, [cartItems]);

  return (
    <Grid container spacing={2} mt={2}>
      <Grid item container xs={12}>
        {cartItems.length > 0 ? (
          cartItems?.map((item, index) => {
            return (
              <Card
                key={index}
                sx={{
                  display: "flex",
                  backgroundColor: theme.palette.primary.light,
                  margin: 1,
                  width: "100%",
                }}
              >
                <CardMedia
                  component="img"
                  sx={{ width: 80, height: "100%", objectFit: "cover" }}
                  image={item?.productImg}
                  alt="item Picture"
                />
                <CardContent
                  sx={{
                    color: theme.palette.primary.dark,
                    width: "100%",
                    paddingBottom: 0,
                  }}
                >
                  <Typography>
                    {item?.productName} - {item?.optionName}
                  </Typography>
                  {/* <Typography fontSize={12}>{item?.description}</Typography> */}
                  <Typography
                    fontWeight="bold"
                    fontStyle={"italic"}
                    fontSize={14}
                  >
                    {item.price > 0 &&
                      `${getCurrencySymbol(
                        paymentInfo.currency
                      )} ${item.price.toFixed(2)}`}
                  </Typography>
                  <Grid container alignItems="center" spacing={1} mt={1}>
                    <Grid item xs={3}>
                      <Button
                        variant="outlined"
                        sx={{ minWidth: "30px" }}
                        onClick={() => handleDecreaseQty(index)}
                      >
                        -
                      </Button>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography>{item.qty}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        variant="outlined"
                        sx={{ minWidth: "30px" }}
                        onClick={() => handleIncreaseQty(index)}
                      >
                        +
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        sx={{ color: theme.palette.secondary.main }}
                        onClick={() => handleRemoveItem(index)}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Grid item container justifyContent={"center"}>
            <Typography
              fontSize={16}
              fontStyle={"italic"}
              color={theme.palette.primary.main}
            >
              No Item In Your Cart At The Moment!
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} color={theme.palette.primary.main}>
        {cartItems.length > 0 && (
          <>
            {subTotal > 0 && (
              <Grid container justifyContent="space-between" mt={3}>
                <Typography
                  fontWeight="bold"
                  color={theme.palette.primary.dark}
                >
                  SUBTOTAL
                </Typography>
                <Typography fontWeight="bold">
                  {businessInfo.paymentInfo.currency} {subTotal.toFixed(2)}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} color={theme.palette.primary.dark}>
              <Typography
                mt={3}
                fontSize={14}
                fontWeight={"weight"}
                fontStyle={"italic"}
              >
                Select How You want Your Item(s) Handled
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="reception-method"
                  name="reception-method"
                  value={selectedReceptionMethod}
                  onChange={handleReceptionMethodChange}
                >
                  <Grid container spacing={2}>
                    {Object.entries(businessInfo.receptionMethod).map(
                      ([key, value]) => {
                        if (value) {
                          return (
                            <Grid item xs={6} key={key}>
                              <FormControlLabel
                                value={key}
                                control={
                                  <Radio
                                    sx={{
                                      color:
                                        selectedReceptionMethod === key
                                          ? theme.palette.secondary.main
                                          : theme.palette.primary.main,
                                    }}
                                  />
                                }
                                label={
                                  key.charAt(0).toUpperCase() + key.slice(1)
                                }
                              />
                            </Grid>
                          );
                        }
                        return null;
                      }
                    )}
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>
            <Typography fontSize={12} color={theme.palette.primary.dark}>
              {businessInfo.receptionMethod.shipping &&
                "Shipping Calculated at Checkout"}
            </Typography>
            <Button
              fullWidth
              variant={"contained"}
              onClick={onCheckoutPressed}
              sx={{
                mt: 2,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.light,
              }}
              disabled={!selectedReceptionMethod}
            >
              CHECKOUT
            </Button>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default Cart;
